import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucidePencil,
  lucidePlus,
  lucideTrash2,
  lucideX,
  lucideCheck,
  lucideTriangleAlert,
  lucideRefreshCw,
  lucideKey,
  lucideClipboard,
  lucideEye,
  lucideEyeOff,
  lucideShieldCheck,
  lucideGlobe,
  lucideServer,
  lucideChartColumn,
  lucideStore,
  lucideExternalLink,
  lucideShare2,
} from '@ng-icons/lucide';

import { UI, type UiBadgeVariant, type UiButtonVariant } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import {
  MerchantApplicationService,
  type MerchantApplication,
  type APIKey,
  type ApplicationStats,
  type ScopeChoice,
} from '../../services/merchant-application.service';

/** Merchant summary loaded alongside the application. */
interface MerchantSummary {
  id: string;
  name: string;
  legal_name: string;
  email: string;
  country_code: string;
  country_name: string;
  is_active: boolean;
  application_count: number;
}

/**
 * Custom detail page for MerchantApplication with:
 * - Merchant overview with link to merchant hub
 * - Application info & settings management
 * - API Keys list, creation, activation, rotation
 * - Secret reveal dialog (shown once on creation)
 * - Activity stats
 */
@Component({
  selector: 'app-merchant-app-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, ...UI],
  templateUrl: './merchant-app-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideArrowLeft,
      lucidePencil,
      lucidePlus,
      lucideTrash2,
      lucideX,
      lucideCheck,
      lucideTriangleAlert,
      lucideRefreshCw,
      lucideKey,
      lucideClipboard,
      lucideEye,
      lucideEyeOff,
      lucideShieldCheck,
      lucideGlobe,
      lucideServer,
      lucideChartColumn,
      lucideStore,
      lucideExternalLink,
      lucideShare2,
    }),
  ],
})
export class MerchantAppDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly appService = inject(MerchantApplicationService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  /* ─── Main data ─── */
  readonly app = signal<MerchantApplication | null>(null);
  readonly merchant = signal<MerchantSummary | null>(null);
  readonly apiKeys = signal<APIKey[]>([]);
  readonly stats = signal<ApplicationStats | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);

  /* ─── Stats period ─── */
  statsPeriod = '30d';

  /* ─── Settings edit state ─── */
  readonly settingsEditing = signal(false);
  readonly settingsSaving = signal(false);
  editAllowedIps: string[] = [];
  editAllowedScopes: string[] = [];
  newIpValue = '';
  editDescription = '';

  /** Scope options from API (fallback empty if older backend). */
  readonly scopeChoices = computed(() => this.app()?.allowed_scope_choices ?? []);

  /* ─── Create Key Modal ─── */
  readonly createKeyOpen = signal(false);
  readonly createKeySaving = signal(false);
  readonly createKeyError = signal<string | null>(null);
  newKeyName = '';
  newKeyExpiry = '';

  /* ─── Secret Reveal Modal ─── */
  readonly secretRevealOpen = signal(false);
  revealedSecret = '';
  revealedKeyName = '';
  secretCopied = false;
  secretShared = false;

  /* ─── Confirm Modal ─── */
  readonly confirmOpen = signal(false);
  readonly confirmSaving = signal(false);
  confirmTitle = '';
  confirmMessage = '';
  confirmBtnLabel = '';
  confirmBtnVariant: UiButtonVariant = 'primary';
  private confirmAction: (() => void) | null = null;

  /* ─── Copy toast ─── */
  readonly copyToast = signal(false);
  private copyToastTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.loadApp();
    this.loadApiKeys();
    this.loadStats();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  // ─── Data Loading ──────────────────────────────────────────

  loadApp(): void {
    this.loading.set(true);
    this.error.set(null);
    this.appService.get(this.id).subscribe({
      next: (data) => {
        this.app.set(data);
        this.loading.set(false);
        // Load merchant info
        if (data.merchant) {
          this.loadMerchant(data.merchant);
        }
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load application.');
        this.loading.set(false);
      },
    });
  }

  loadMerchant(merchantId: string): void {
    this.appService.getMerchant(merchantId).subscribe({
      next: (data) => this.merchant.set(data as unknown as MerchantSummary),
      error: () => this.merchant.set(null),
    });
  }

  loadApiKeys(): void {
    this.appService.getApiKeys(this.id).subscribe({
      next: (keys) => this.apiKeys.set(keys),
      error: () => this.apiKeys.set([]),
    });
  }

  loadStats(): void {
    this.appService.getStats(this.id, this.statsPeriod).subscribe({
      next: (data) => this.stats.set(data),
      error: () => this.stats.set(null),
    });
  }

  onPeriodChange(): void {
    this.loadStats();
  }

  // ─── Settings Panel ────────────────────────────────────────

  startEditSettings(): void {
    const a = this.app();
    if (!a) return;
    this.editAllowedIps = [...(a.allowed_ips || [])];
    this.editAllowedScopes = [...(a.allowed_scopes || [])];
    this.editDescription = a.description || '';
    this.newIpValue = '';
    this.settingsEditing.set(true);
  }

  cancelEditSettings(): void {
    this.settingsEditing.set(false);
  }

  addIp(): void {
    const ip = this.newIpValue.trim();
    if (ip && !this.editAllowedIps.includes(ip)) {
      this.editAllowedIps = [...this.editAllowedIps, ip];
    }
    this.newIpValue = '';
  }

  removeIp(ip: string): void {
    this.editAllowedIps = this.editAllowedIps.filter((i) => i !== ip);
  }

  toggleScope(scope: string): void {
    const ALL = 'all_scopes';
    if (scope === ALL) {
      if (this.editAllowedScopes.includes(ALL)) {
        this.editAllowedScopes = [];
      } else {
        this.editAllowedScopes = [ALL];
      }
      return;
    }
    let next = [...this.editAllowedScopes].filter((s) => s !== ALL);
    if (next.includes(scope)) {
      next = next.filter((s) => s !== scope);
    } else {
      next.push(scope);
    }
    this.editAllowedScopes = next;
  }

  isScopeSelected(scope: string): boolean {
    return this.editAllowedScopes.includes(scope);
  }

  scopeLabel(value: string): string {
    const choices = this.app()?.allowed_scope_choices ?? [];
    const found = choices.find((c: ScopeChoice) => c.value === value);
    return found?.label ?? value;
  }

  saveSettings(): void {
    const a = this.app();
    if (!a) return;

    this.settingsSaving.set(true);
    this.error.set(null);

    this.appService
      .update(a.id, {
        allowed_ips: this.editAllowedIps,
        allowed_scopes: this.editAllowedScopes,
        description: this.editDescription,
      })
      .subscribe({
        next: (updated) => {
          this.app.set(updated);
          this.settingsSaving.set(false);
          this.settingsEditing.set(false);
          this.showSuccess('Settings saved.');
        },
        error: (err) => {
          this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to save settings.');
          this.settingsSaving.set(false);
        },
      });
  }

  // ─── Activation Toggle ─────────────────────────────────────

  confirmToggleActive(): void {
    const a = this.app();
    if (!a) return;
    const activate = !a.is_active;
    this.openConfirm(
      activate ? 'Activate Application' : 'Deactivate Application',
      activate
        ? `Activate "${a.name}"? API keys under this application will become usable.`
        : `Deactivate "${a.name}"? All API keys under this application will stop working.`,
      activate ? 'Activate' : 'Deactivate',
      activate ? 'primary' : 'danger',
      () => {
        const action$ = activate ? this.appService.activate(a.id) : this.appService.deactivate(a.id);
        action$.subscribe({
          next: (updated) => {
            this.app.set(updated);
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.showSuccess(activate ? 'Application activated.' : 'Application deactivated.');
          },
          error: (err) => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to update status.');
          },
        });
      },
    );
  }

  // ─── API Key Management ────────────────────────────────────

  openCreateKeyModal(): void {
    this.newKeyName = '';
    this.newKeyExpiry = '';
    this.createKeyError.set(null);
    this.createKeyOpen.set(true);
  }

  closeCreateKeyModal(): void {
    this.createKeyOpen.set(false);
  }

  submitCreateKey(): void {
    if (!this.newKeyName.trim()) {
      this.createKeyError.set('Key name is required.');
      return;
    }

    this.createKeySaving.set(true);
    this.createKeyError.set(null);

    const data: { application: string; name: string; expires_at?: string | null } = {
      application: this.id,
      name: this.newKeyName.trim(),
    };
    if (this.newKeyExpiry) {
      data.expires_at = new Date(this.newKeyExpiry).toISOString();
    }

    this.appService.createApiKey(data).subscribe({
      next: (key) => {
        this.createKeySaving.set(false);
        this.closeCreateKeyModal();
        this.loadApiKeys();

        // Show the secret reveal modal
        if (key.secret) {
          this.revealedSecret = key.secret;
          this.revealedKeyName = key.name;
          this.secretCopied = false;
          this.secretShared = false;
          this.secretRevealOpen.set(true);
        } else {
          this.showSuccess('API Key created.');
        }
      },
      error: (err) => {
        const detail = err?.error;
        if (detail && typeof detail === 'object') {
          const msgs = Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
          this.createKeyError.set(msgs.join(' | '));
        } else {
          this.createKeyError.set(err?.message ?? 'Failed to create API key.');
        }
        this.createKeySaving.set(false);
      },
    });
  }

  closeSecretReveal(): void {
    this.secretRevealOpen.set(false);
    this.revealedSecret = '';
    this.revealedKeyName = '';
    this.showSuccess('API Key created. Make sure you saved the secret.');
  }

  copySecret(): void {
    navigator.clipboard.writeText(this.revealedSecret).then(() => {
      this.secretCopied = true;
    });
  }

  shareSecret(): void {
    const a = this.app();
    const m = this.merchant();
    const lines = [
      '──────────────────────────────────',
      '  API KEY CREDENTIALS',
      '──────────────────────────────────',
      '',
      `Merchant:        ${m?.name ?? a?.merchant_name ?? '—'}`,
      `Application:     ${a?.name ?? '—'}`,
      `Application ID:  ${a?.id ?? '—'}`,
      `Client ID:       ${a?.client_id ?? '—'}`,
      `Environment:     ${a?.environment ?? '—'}`,
      '',
      `Key Name:        ${this.revealedKeyName}`,
      `Secret:          ${this.revealedSecret}`,
      '',
      '──────────────────────────────────',
      '  IMPORTANT: Store this secret in a',
      '  safe place. It cannot be recovered.',
      '──────────────────────────────────',
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      this.secretCopied = true;
      this.secretShared = true;
    });
  }

  confirmToggleKeyActive(key: APIKey): void {
    const activate = !key.is_active;
    this.openConfirm(
      activate ? 'Activate API Key' : 'Deactivate API Key',
      activate
        ? `Activate key "${key.name}" (${key.secret_prefix}...)?`
        : `Deactivate key "${key.name}" (${key.secret_prefix}...)? It will stop authenticating requests.`,
      activate ? 'Activate' : 'Deactivate',
      activate ? 'primary' : 'danger',
      () => {
        const action$ = activate ? this.appService.activateKey(key.id) : this.appService.deactivateKey(key.id);
        action$.subscribe({
          next: () => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.loadApiKeys();
            this.showSuccess(activate ? 'Key activated.' : 'Key deactivated.');
          },
          error: (err) => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.error.set(err?.message ?? 'Failed to update key.');
          },
        });
      },
    );
  }

  confirmRotateKey(key: APIKey): void {
    this.openConfirm(
      'Rotate API Key',
      `Rotate key "${key.name}" (${key.secret_prefix}...)? A new key will be created and this key will be deactivated.`,
      'Rotate',
      'primary',
      () => {
        this.appService.rotateKey(key.id).subscribe({
          next: (newKey) => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.loadApiKeys();

            if (newKey.secret) {
              this.revealedSecret = newKey.secret;
              this.revealedKeyName = newKey.name;
              this.secretCopied = false;
              this.secretShared = false;
              this.secretRevealOpen.set(true);
            } else {
              this.showSuccess('Key rotated.');
            }
          },
          error: (err) => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.error.set(err?.message ?? 'Failed to rotate key.');
          },
        });
      },
    );
  }

  confirmDeleteKey(key: APIKey): void {
    this.openConfirm(
      'Delete API Key',
      `Permanently delete key "${key.name}" (${key.secret_prefix}...)? This cannot be undone.`,
      'Delete',
      'danger',
      () => {
        this.appService.deleteKey(key.id).subscribe({
          next: () => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.loadApiKeys();
            this.showSuccess('Key deleted.');
          },
          error: (err) => {
            this.confirmSaving.set(false);
            this.closeConfirm();
            this.error.set(err?.message ?? 'Failed to delete key.');
          },
        });
      },
    );
  }

  // ─── Confirmation Modal ────────────────────────────────────

  openConfirm(title: string, message: string, btnLabel: string, btnClass: UiButtonVariant, action: () => void): void {
    this.confirmTitle = title;
    this.confirmMessage = message;
    this.confirmBtnLabel = btnLabel;
    this.confirmBtnVariant = btnClass;
    this.confirmAction = action;
    this.confirmSaving.set(false);
    this.confirmOpen.set(true);
  }

  closeConfirm(): void {
    this.confirmOpen.set(false);
    this.confirmAction = null;
  }

  executeConfirm(): void {
    if (this.confirmAction) {
      this.confirmSaving.set(true);
      this.confirmAction();
    }
  }

  // ─── Helpers ───────────────────────────────────────────────

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      if (this.copyToastTimeout) clearTimeout(this.copyToastTimeout);
      this.copyToast.set(true);
      this.copyToastTimeout = setTimeout(() => this.copyToast.set(false), 1500);
    });
  }

  getEnvVariant(env: string): UiBadgeVariant {
    switch (env) {
      case 'production':
        return 'info';
      case 'sandbox':
        return 'warning';
      case 'development':
        return 'brand';
      default:
        return 'neutral';
    }
  }

  formatDate(value: string | null): string {
    if (!value) return '—';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return (
        d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
        ' ' +
        d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      );
    } catch {
      return value;
    }
  }

  formatAmount(value: number): string {
    if (!value) return '0.00';
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getTotalTransactions(): number {
    const s = this.stats();
    if (!s) return 0;
    const tx = s.transactions;
    return tx.deposits.total + tx.payouts.total + tx.refunds.total + tx.remittances.total;
  }

  getTotalSuccessAmount(): number {
    const s = this.stats();
    if (!s) return 0;
    const tx = s.transactions;
    return tx.deposits.success_amount + tx.payouts.success_amount + tx.refunds.success_amount + tx.remittances.success_amount;
  }

  private showSuccess(msg: string): void {
    this.success.set(msg);
    setTimeout(() => this.success.set(null), 3000);
  }
}
